import React, { createContext, useState, useEffect, ReactNode } from 'react'

type Theme = 'light' | 'dark'

interface ThemeContextType {
    theme: Theme
    toggleTheme: () => void
    applyTheme: (theme: Theme) => void
}

export const ThemeContext = createContext<ThemeContextType>({
    theme: 'dark',
    toggleTheme: () => {
        //nop
    },
    applyTheme: () => {
        //nop
    },
})

const getThemeFromCookie = (): Theme | null => {
    const cookie = document.cookie
        .split('; ')
        .find((row) => row.startsWith('user_theme='))
    const value = cookie?.split('=')[1]
    return value === 'light' || value === 'dark' ? value : null
}

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
    const [theme, setTheme] = useState<Theme>('dark')
    const [ready, setReady] = useState(false)

    const applyTheme = (t: Theme) => {
        setTheme(t)
        localStorage.setItem('theme', t)
        document.cookie = `user_theme=${t}; path=/; domain=.noxsys.ru`
        if (t === 'dark') {
            document.documentElement.classList.add('dark')
        } else {
            document.documentElement.classList.remove('dark')
        }
    }

    const getInitialTheme = (): Theme => {
        // 1. сначала из cookie, установленной сервером
        const cookieTheme = getThemeFromCookie()
        if (cookieTheme) return cookieTheme

        // 2. потом из localStorage (если пользователь вручную поменял позже)
        const storedTheme = localStorage.getItem('theme') as Theme | null
        if (storedTheme === 'light' || storedTheme === 'dark')
            return storedTheme

        // 3. по умолчанию
        return 'dark'
    }

    useEffect(() => {
        const themeInit = getInitialTheme()
        applyTheme(themeInit)
        setReady(true)
    }, [])

    const toggleTheme = async () => {
        const newTheme: Theme = theme === 'dark' ? 'light' : 'dark'
        applyTheme(newTheme)

        try {
            await fetch(
                process.env.REACT_APP_API_URL + `/api/backend/v1/user`,
                {
                    method: 'PUT',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                    },
                    body: JSON.stringify({ theme: newTheme }),
                }
            )
        } catch (error) {
            console.error('Ошибка при обновлении темы на сервере', error)
        }
    }

    if (!ready) return null // или <Loader /> если хочешь прелоадер

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme, applyTheme }}>
            {children}
        </ThemeContext.Provider>
    )
}
